import { motion } from 'framer-motion';
import React, { useState } from 'react';
import {
    FiBarChart2,
    FiBell,
    FiChevronDown,
    FiEdit,
    FiFileText,
    FiGrid,
    FiLogOut,
    FiMail,
    FiMenu,
    FiSettings,
    FiUser,
    FiUsers,
    FiX,
    FiZap
} from 'react-icons/fi';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

const Layout = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [profileDropdownOpen, setProfileDropdownOpen] = useState(false);
  const location = useLocation();
  const { user, logout } = useAuth();

  const handleLogout = () => {
    logout();
    setProfileDropdownOpen(false);
  };

  const navigation = [
    { 
      name: 'Dashboard', 
      href: '/dashboard', 
      icon: FiGrid, 
      current: location.pathname === '/dashboard' || location.pathname === '/',
      gradient: 'from-cyan-400 via-blue-500 to-indigo-600',
      bgGradient: 'from-cyan-500/20 via-blue-500/20 to-indigo-600/20'
    },
    { 
      name: 'Analytics', 
      href: '/analytics', 
      icon: FiBarChart2, 
      current: location.pathname === '/analytics',
      gradient: 'from-emerald-400 via-green-500 to-teal-600',
      bgGradient: 'from-emerald-500/20 via-green-500/20 to-teal-600/20'
    },
    { 
      name: 'Subscribers', 
      href: '/subscribers', 
      icon: FiMail, 
      current: location.pathname === '/subscribers',
      gradient: 'from-violet-400 via-purple-500 to-pink-600',
      bgGradient: 'from-violet-500/20 via-purple-500/20 to-pink-600/20'
    },
    { 
      name: 'Client Intake Forms', 
      href: '/client-intake', 
      icon: FiUsers, 
      current: location.pathname === '/client-intake' || location.pathname === '/client-intake-forms',
      gradient: 'from-blue-400 via-purple-500 to-indigo-600',
      bgGradient: 'from-blue-500/20 via-purple-500/20 to-indigo-600/20'
    },
    { 
      name: 'Resumes', 
      href: '/resumes', 
      icon: FiFileText, 
      current: location.pathname === '/resumes',
      gradient: 'from-amber-400 via-orange-500 to-red-600',
      bgGradient: 'from-amber-500/20 via-orange-500/20 to-red-600/20'
    },
    { 
      name: 'Content Creator', 
      href: '/content-creator', 
      icon: FiEdit, 
      current: location.pathname === '/content-creator',
      gradient: 'from-pink-400 via-rose-500 to-red-600',
      bgGradient: 'from-pink-500/20 via-rose-500/20 to-red-600/20'
    },
  ];

  const notifications = [
    { id: 1, title: 'New subscriber', message: 'John Doe subscribed to newsletter', time: '5 min ago', unread: true },
    { id: 2, title: 'Resume uploaded', message: 'Sarah Johnson uploaded resume', time: '15 min ago', unread: true },
    { id: 3, title: 'Website traffic spike', message: 'Traffic increased by 25%', time: '1 hour ago', unread: false },
  ];

  const floatingAnimation = {
    y: [-8, 8, -8],
    transition: {
      duration: 4,
      repeat: Infinity,
      ease: "easeInOut"
    }
  };

  const sidebarVariants = {
    hidden: { x: -300, opacity: 0 },
    visible: {
      x: 0,
      opacity: 1,
      transition: {
        type: "spring",
        damping: 25,
        stiffness: 120,
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { x: -20, opacity: 0 },
    visible: {
      x: 0,
      opacity: 1,
      transition: {
        type: "spring",
        damping: 20,
        stiffness: 120
      }
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-gray-900 to-black relative overflow-hidden">
      {/* Spectacular Animated Background */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -right-40 w-96 h-96 bg-purple-500 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-blob"></div>
        <div className="absolute -bottom-40 -left-40 w-96 h-96 bg-cyan-500 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-blob animation-delay-2000"></div>
        <div className="absolute top-1/2 left-1/3 w-96 h-96 bg-pink-500 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-blob animation-delay-4000"></div>
        <div className="absolute bottom-1/4 right-1/3 w-80 h-80 bg-emerald-500 rounded-full mix-blend-multiply filter blur-xl opacity-15 animate-blob animation-delay-1000"></div>
      </div>

      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar - Enhanced Spectacular Design */}
      <div className={`hidden lg:flex lg:w-80 lg:flex-col lg:fixed lg:inset-y-0 z-50`}>
        <motion.div 
          className="flex flex-col flex-grow bg-gradient-to-br from-slate-800/95 via-gray-800/95 to-slate-800/95 backdrop-blur-xl border-r border-white/10 shadow-2xl relative"
          initial="hidden"
          animate="visible"
          variants={sidebarVariants}
        >
          {/* Floating elements in sidebar */}
          <motion.div
            className="absolute top-20 right-8 w-2 h-2 bg-cyan-400 rounded-full"
            animate={floatingAnimation}
          />
          <motion.div
            className="absolute bottom-40 left-8 w-1.5 h-1.5 bg-purple-400 rounded-full"
            animate={{ ...floatingAnimation, transition: { ...floatingAnimation.transition, delay: 1 } }}
          />

          {/* Enhanced Logo Section */}
          <motion.div 
            className="flex h-20 items-center justify-center px-6 border-b border-white/10 relative"
            variants={itemVariants}
          >
            <div className="flex items-center space-x-4">
              <motion.div 
                className="w-12 h-12 bg-gradient-to-br from-cyan-500 via-blue-500 to-indigo-600 rounded-2xl flex items-center justify-center shadow-2xl"
                whileHover={{ scale: 1.1, rotate: 5 }}
                transition={{ duration: 0.3 }}
              >
                <motion.span 
                  className="text-white font-bold text-2xl font-display"
                  animate={{ rotateY: [0, 360] }}
                  transition={{ duration: 4, repeat: Infinity, ease: "linear" }}
                >
                  I
                </motion.span>
              </motion.div>
              <div>
                <motion.h1 
                  className="text-xl font-bold bg-gradient-to-r from-cyan-400 via-blue-400 to-indigo-400 bg-clip-text text-transparent font-display"
                  animate={{ backgroundPosition: ['0%', '100%', '0%'] }}
                  transition={{ duration: 3, repeat: Infinity }}
                >
                  Innofusion
                </motion.h1>
                <p className="text-sm text-gray-400 font-body -mt-1">Premium Dashboard</p>
              </div>
            </div>
          </motion.div>

          {/* Enhanced Navigation */}
          <nav className="flex-1 px-4 py-6 space-y-3 overflow-y-auto">
            {navigation.map((item, index) => (
              <motion.div
                key={item.name}
                variants={itemVariants}
                whileHover={{ x: 8, scale: 1.02 }}
                transition={{ duration: 0.2 }}
              >
                <Link
                  to={item.href}
                  onClick={() => setSidebarOpen(false)}
                  className={`group flex items-center px-4 py-4 text-sm font-medium rounded-2xl transition-all duration-300 relative overflow-hidden ${
                    item.current
                      ? `bg-gradient-to-r ${item.bgGradient} border border-white/20 text-white shadow-2xl backdrop-blur-xl`
                      : 'text-gray-300 hover:bg-white/10 hover:text-white backdrop-blur-sm border border-transparent hover:border-white/10'
                  }`}
                >
                  {/* Gradient overlay for active state */}
                  {item.current && (
                    <div className={`absolute inset-0 bg-gradient-to-r ${item.gradient} opacity-10 rounded-2xl`}></div>
                  )}
                  
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center mr-4 relative z-10 ${
                    item.current 
                      ? `bg-gradient-to-br ${item.gradient} shadow-lg` 
                      : 'bg-white/5 group-hover:bg-white/10'
                  }`}>
                    <item.icon
                      className={`${
                        item.current ? 'text-white' : 'text-gray-400 group-hover:text-white'
                      }`}
                      size={20}
                    />
                  </div>
                  <span className="font-body font-semibold relative z-10">{item.name}</span>
                  
                  {item.current && (
                    <motion.div
                      layoutId="activeTab"
                      className="absolute right-4 w-2 h-2 bg-white rounded-full shadow-lg"
                      animate={{ scale: [1, 1.2, 1] }}
                      transition={{ duration: 2, repeat: Infinity }}
                    />
                  )}

                  {/* Animated border */}
                  <div className="absolute inset-0 rounded-2xl bg-gradient-to-r from-transparent via-white/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                </Link>
              </motion.div>
            ))}
          </nav>

          {/* Enhanced User Section */}
          <motion.div 
            className="px-4 py-6 border-t border-white/10"
            variants={itemVariants}
          >
            <motion.div 
              className="flex items-center space-x-4 p-4 rounded-2xl bg-gradient-to-r from-purple-500/20 via-pink-500/20 to-red-500/20 backdrop-blur-xl border border-white/10 cursor-pointer hover:from-purple-500/30 hover:via-pink-500/30 hover:to-red-500/30 transition-all duration-300 group"
              whileHover={{ scale: 1.02, y: -2 }}
              whileTap={{ scale: 0.98 }}
            >
              <motion.div 
                className="w-12 h-12 bg-gradient-to-br from-purple-500 via-pink-500 to-red-600 rounded-2xl flex items-center justify-center shadow-2xl group-hover:scale-110 transition-transform duration-300"
                animate={{ rotate: [0, 360] }}
                transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
              >
                <FiUser className="text-white" size={20} />
              </motion.div>
              <div className="flex-1 min-w-0">
                <p className="text-base font-bold text-white font-heading truncate">Admin User</p>
                <p className="text-sm text-gray-300 font-body truncate">admin@innofusion.com</p>
              </div>
              <motion.div
                animate={{ x: [0, 5, 0] }}
                transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
              >
                <FiZap className="text-yellow-400" size={16} />
              </motion.div>
            </motion.div>
          </motion.div>
        </motion.div>
      </div>

      {/* Enhanced Mobile Sidebar */}
      <motion.div
        initial={false}
        animate={{ x: sidebarOpen ? 0 : '-100%' }}
        className={`lg:hidden fixed inset-y-0 left-0 z-50 w-80 bg-gradient-to-br from-slate-800/95 via-gray-800/95 to-slate-800/95 backdrop-blur-xl border-r border-white/10 shadow-2xl transition-all duration-300 ease-in-out ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        <div className="flex h-full flex-col relative">
          {/* Floating elements in mobile sidebar */}
          <motion.div
            className="absolute top-20 right-8 w-2 h-2 bg-cyan-400 rounded-full"
            animate={floatingAnimation}
          />

          {/* Enhanced Mobile Logo */}
          <div className="flex h-20 items-center justify-between px-6 border-b border-white/10">
            <div className="flex items-center space-x-4">
              <motion.div 
                className="w-10 h-10 bg-gradient-to-br from-cyan-500 via-blue-500 to-indigo-600 rounded-xl flex items-center justify-center shadow-lg"
                whileHover={{ scale: 1.1, rotate: 5 }}
              >
                <span className="text-white font-bold text-lg font-display">I</span>
              </motion.div>
              <div>
                <h1 className="text-lg font-bold bg-gradient-to-r from-cyan-400 to-blue-400 bg-clip-text text-transparent font-display">
                  Innofusion
                </h1>
                <p className="text-xs text-gray-400 -mt-1 font-body">Premium Dashboard</p>
              </div>
            </div>
            <motion.button
              onClick={() => setSidebarOpen(false)}
              className="p-3 rounded-xl text-gray-400 hover:text-white hover:bg-white/10 transition-all duration-300 border border-transparent hover:border-white/20"
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
            >
              <FiX size={20} />
            </motion.button>
          </div>

          {/* Mobile Navigation */}
          <nav className="flex-1 px-4 py-6 space-y-3 overflow-y-auto">
            {navigation.map((item, index) => (
              <motion.div
                key={item.name}
                initial={{ x: -20, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                transition={{ delay: index * 0.1 }}
              >
                <Link
                  to={item.href}
                  onClick={() => setSidebarOpen(false)}
                  className={`group flex items-center px-4 py-4 text-sm font-medium rounded-2xl transition-all duration-300 relative ${
                    item.current
                      ? `bg-gradient-to-r ${item.bgGradient} border border-white/20 text-white shadow-2xl`
                      : 'text-gray-300 hover:bg-white/10 hover:text-white'
                  }`}
                >
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center mr-4 ${
                    item.current 
                      ? `bg-gradient-to-br ${item.gradient} shadow-lg` 
                      : 'bg-white/5 group-hover:bg-white/10'
                  }`}>
                    <item.icon
                      className={`${
                        item.current ? 'text-white' : 'text-gray-400 group-hover:text-white'
                      }`}
                      size={20}
                    />
                  </div>
                  <span className="font-body font-semibold">{item.name}</span>
                  {item.current && (
                    <motion.div
                      layoutId="activeTabMobile"
                      className="absolute right-4 w-2 h-2 bg-white rounded-full shadow-lg"
                      animate={{ scale: [1, 1.2, 1] }}
                      transition={{ duration: 2, repeat: Infinity }}
                    />
                  )}
                </Link>
              </motion.div>
            ))}
          </nav>

          {/* Mobile User section */}
          <div className="px-4 py-6 border-t border-white/10">
            <motion.div 
              className="flex items-center space-x-4 p-4 rounded-2xl bg-gradient-to-r from-purple-500/20 via-pink-500/20 to-red-500/20 backdrop-blur-xl border border-white/10"
              whileHover={{ scale: 1.02 }}
            >
              <div className="w-12 h-12 bg-gradient-to-br from-purple-500 via-pink-500 to-red-600 rounded-2xl flex items-center justify-center shadow-lg">
                <FiUser className="text-white" size={20} />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-base font-bold text-white font-heading truncate">{user?.username || 'Admin User'}</p>
                <p className="text-sm text-gray-300 font-body truncate">{user?.role || 'Administrator'}</p>
              </div>
              <motion.button
                onClick={handleLogout}
                className="p-3 rounded-xl text-red-400 hover:text-red-300 hover:bg-red-500/10 transition-all duration-300"
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
                title="Sign Out"
              >
                <FiLogOut size={20} />
              </motion.button>
            </motion.div>
          </div>
        </div>
      </motion.div>

      {/* Main content */}
      <div className="lg:pl-80 flex-1 flex flex-col relative z-10">
        {/* Enhanced Header */}
        <header className="bg-gradient-to-r from-slate-800/95 via-gray-800/95 to-slate-800/95 backdrop-blur-xl border-b border-white/10 sticky top-0 z-40 shadow-2xl">
          <div className="flex h-16 items-center justify-between px-6 relative">
            {/* Floating elements in header */}
            <motion.div
              className="absolute top-3 right-20 w-1.5 h-1.5 bg-cyan-400 rounded-full"
              animate={floatingAnimation}
            />

            <div className="flex items-center space-x-4">
              <motion.button
                onClick={() => setSidebarOpen(true)}
                className="lg:hidden p-3 text-gray-400 hover:text-white rounded-xl hover:bg-white/10 transition-all duration-300 border border-transparent hover:border-white/20"
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
              >
                <FiMenu size={20} />
              </motion.button>
              <motion.h1 
                className="text-2xl font-bold bg-gradient-to-r from-cyan-400 via-purple-400 to-pink-400 bg-clip-text text-transparent font-display"
                animate={{ backgroundPosition: ['0%', '100%', '0%'] }}
                transition={{ duration: 4, repeat: Infinity }}
              >
                {navigation.find(item => item.current)?.name || 'Dashboard'}
              </motion.h1>
            </div>

            <div className="flex items-center space-x-4">
              {/* Enhanced Notifications */}
              <motion.div 
                className="relative"
                whileHover={{ scale: 1.05 }}
              >
                <motion.button 
                  className="p-3 text-gray-400 hover:text-white rounded-xl hover:bg-white/10 transition-all duration-300 relative border border-transparent hover:border-white/20"
                  whileHover={{ y: -2 }}
                >
                  <FiBell size={20} />
                  <motion.span 
                    className="absolute -top-1 -right-1 w-3 h-3 bg-gradient-to-r from-red-500 to-pink-600 rounded-full shadow-lg"
                    animate={{ scale: [1, 1.3, 1] }}
                    transition={{ duration: 2, repeat: Infinity }}
                  />
                  <motion.span 
                    className="absolute -top-1 -right-1 w-3 h-3 bg-red-400 rounded-full animate-ping"
                  />
                </motion.button>
              </motion.div>

              {/* Enhanced Profile dropdown */}
              <div className="relative">
                <motion.button
                  onClick={() => setProfileDropdownOpen(!profileDropdownOpen)}
                  className="flex items-center space-x-3 p-3 text-gray-300 hover:text-white rounded-xl hover:bg-white/10 transition-all duration-300 border border-transparent hover:border-white/20"
                  whileHover={{ scale: 1.02, y: -2 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <motion.div 
                    className="w-9 h-9 bg-gradient-to-br from-purple-500 via-pink-500 to-red-600 rounded-xl flex items-center justify-center shadow-lg"
                    animate={{ rotate: [0, 360] }}
                    transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
                  >
                    <FiUser className="text-white" size={16} />
                  </motion.div>
                  <span className="hidden md:block font-medium font-body text-sm">{user?.username || 'Admin'}</span>
                  <motion.div
                    animate={{ rotate: profileDropdownOpen ? 180 : 0 }}
                    transition={{ duration: 0.3 }}
                  >
                    <FiChevronDown size={16} />
                  </motion.div>
                </motion.button>

                {profileDropdownOpen && (
                  <motion.div
                    initial={{ opacity: 0, y: -10, scale: 0.95 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    className="absolute right-0 mt-3 w-64 bg-gradient-to-br from-slate-800/95 via-gray-800/95 to-slate-800/95 backdrop-blur-xl rounded-2xl shadow-2xl border border-white/20 py-3 z-50"
                  >
                    {/* User Info Section */}
                    <div className="px-5 py-3 border-b border-white/10">
                      <div className="flex items-center space-x-3">
                        <div className="w-10 h-10 bg-gradient-to-br from-purple-500 via-pink-500 to-red-600 rounded-xl flex items-center justify-center shadow-lg">
                          <FiUser className="text-white" size={18} />
                        </div>
                        <div>
                          <p className="text-sm font-semibold text-white">{user?.username || 'Admin'}</p>
                          <p className="text-xs text-gray-400">{user?.role || 'Administrator'}</p>
                        </div>
                      </div>
                      {user?.loginTime && (
                        <div className="mt-2 text-xs text-gray-500">
                          Logged in: {new Date(user.loginTime).toLocaleString()}
                        </div>
                      )}
                    </div>

                    {/* Menu Items */}
                    <motion.button 
                      className="w-full flex items-center px-5 py-3 text-sm text-gray-300 hover:bg-white/10 hover:text-white transition-all duration-200 font-body group text-left"
                      whileHover={{ x: 5 }}
                      onClick={() => setProfileDropdownOpen(false)}
                    >
                      <FiUser className="mr-3 text-cyan-400 group-hover:scale-110 transition-transform" size={16} />
                      Profile Settings
                    </motion.button>
                    <motion.button 
                      className="w-full flex items-center px-5 py-3 text-sm text-gray-300 hover:bg-white/10 hover:text-white transition-all duration-200 font-body group text-left"
                      whileHover={{ x: 5 }}
                      onClick={() => setProfileDropdownOpen(false)}
                    >
                      <FiSettings className="mr-3 text-purple-400 group-hover:scale-110 transition-transform" size={16} />
                      Preferences
                    </motion.button>
                    <hr className="my-2 border-white/10" />
                    <motion.button 
                      onClick={handleLogout}
                      className="w-full flex items-center px-5 py-3 text-sm text-red-400 hover:bg-red-500/10 hover:text-red-300 transition-all duration-200 font-body group text-left"
                      whileHover={{ x: 5 }}
                    >
                      <FiLogOut className="mr-3 group-hover:scale-110 transition-transform" size={16} />
                      Sign Out
                    </motion.button>
                  </motion.div>
                )}
              </div>
            </div>
          </div>
        </header>

        {/* Page content */}
        <main className="flex-1 bg-transparent">
          {children}
        </main>
      </div>
    </div>
  );
};

export default Layout; 